import { useEffect, useRef, useState } from 'react';
import { progressLine } from './site3dModel';
import { KIND_LABEL, shortHuman } from './siteTimelineModel';
import StageFrame from './SiteStage';

const loadEngine = () => import('./site3dEngine').then((m) => m.createSite3D);

// items: [{ url, format, kind, date?, label? }] — one file, or two dates side by side.
// mode 'changes': two point clouds measured into one pane, coloured by change.
export default function Model3DView({ items, onClose, title, mode = 'side', onChanges, engineLoader = loadEngine }) {
  const paneRefs = useRef([]);
  const handle = useRef(null);
  const changes = mode === 'changes' && items.length === 2;
  const panes = changes ? 1 : items.length;
  const [state, setState] = useState(() => Array.from({ length: panes }, () => ({ status: 'loading', loaded: 0, total: 0 })));
  const key = `${changes ? 'changes:' : ''}${items.map((i) => i.url).join('|')}`;
  const changesCb = useRef(onChanges);
  changesCb.current = onChanges;

  useEffect(() => {
    let cancelled = false;
    const patch = (i, next) => setState((s) => s.map((x, j) => (j === i ? { ...x, ...next } : x)));
    setState(Array.from({ length: panes }, () => ({ status: 'loading', loaded: 0, total: 0 })));
    engineLoader()
      .then((create) => create(paneRefs.current.slice(0, panes), items, {
        onProgress: (i, loaded, total) => patch(i, { loaded, total }),
        onParsing: (i) => patch(i, { status: 'parsing' }),
        onReady: (i, stats) => patch(i, { status: 'ready', stats }),
        onError: (i, message) => { patch(i, { status: 'error', message }); if (changes && changesCb.current) changesCb.current({ error: message }); },
        onChanges: (summary) => { if (changesCb.current) changesCb.current({ summary }); },
      }, { mode: changes ? 'changes' : 'side' }))
      .then((h) => {
        if (cancelled) h.dispose();
        else handle.current = h;
      })
      .catch(() => {
        if (!cancelled) setState(Array.from({ length: panes }, () => ({ status: 'error', message: "3D view isn't available in this browser." })));
      });
    return () => {
      cancelled = true;
      if (handle.current) handle.current.dispose();
      handle.current = null;
    };
  }, [key]);

  const pair = !changes && items.length > 1;
  const shown = changes
    ? [{ ...items[1], kindLabel: `Changes since ${shortHuman(items[0].date) || 'the earlier date'}` }]
    : items.map((it) => ({ ...it, kindLabel: KIND_LABEL[it.kind] || '3D view' }));
  const hint = changes ? 'Orange is higher, blue is lower. Drag to orbit.' : `${pair ? 'Both dates move together. ' : ''}Drag to orbit. Pinch or scroll to zoom.`;
  return (
    <StageFrame testid="site-3d" paneTestid="site-3d-pane-" title={title || '3D view'}
      items={shown}
      state={state.map((st) => ({ ...st, progress: st.status === 'parsing' ? 'Preparing' : progressLine(st.loaded, st.total) }))}
      paneRefs={paneRefs} onReset={() => handle.current && handle.current.reset()} onClose={onClose}
      hint={hint} />
  );
}
